import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import { getErrorMessage, useMutationCompletionHandler } from '@ts-core/lib/apollo';

import { UploadProgressState } from 'core/components/UploadProgressIndicator';
import { useFileUploader } from 'core/components/FileUploader';

import { createDocumentMutation } from '../graphql/mutations';
import { getDocumentsBySubjectQuery } from '../graphql/queries';

export default function useDocumentUploader({ courseId, subjectId }) {
  const apolloClient = useApolloClient();
  const { addItemToCache } = useMutationCompletionHandler();

  const createDocument = useCallback(
    async ({ input, setState, setError }) => {
      setState(UploadProgressState.saving);

      try {
        await apolloClient.mutate({
          mutation: createDocumentMutation,
          variables: {
            courseId,
            subjectId,
            input,
          },
          update: addItemToCache({
            query: getDocumentsBySubjectQuery,
            queryField: 'documents',
            variables: { courseId, subjectId },
            mutationField: 'document',
          }),
        });

        setState(UploadProgressState.success);

        setTimeout(() => {
          setState(UploadProgressState.idle);
        }, 3000);
      } catch (mutationError) {
        setState(UploadProgressState.failed);
        setError(getErrorMessage({ error: mutationError }));
      }
    },
    [addItemToCache, apolloClient, courseId, subjectId]
  );

  const getFileType = useCallback(() => 'documents', []);

  const { error, state, fileName, progress, onFileSelected, onClose } = useFileUploader({
    getFileType,
    onFileUploaded: createDocument,
  });

  return {
    fileName,
    error,
    progress,
    state,

    onFileSelected,
    onClose,
  };
}
